"use client";

import { useId, useState } from "react";
import { useRouter } from "next/navigation";
import { KnowledgeSearchView } from "./KnowledgeSearchView";
import { PaletteFooter } from "./PaletteFooter";
import { PaletteResults } from "./PaletteResults";
import { PaletteSearchHeader } from "./PaletteSearchHeader";
import { PaletteToast } from "./PaletteToast";
import { usePaletteActions } from "./usePaletteActions";
import { usePaletteData } from "./usePaletteData";
import { usePaletteFocus } from "./usePaletteFocus";
import { usePaletteItems } from "./usePaletteItems";
import { usePaletteKeyboard } from "./usePaletteKeyboard";
import { usePaletteOpenState } from "./usePaletteOpenState";
import { usePaletteView } from "./usePaletteView";

export interface CommandPaletteProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function CommandPalette({
  open: controlledOpen,
  onOpenChange,
}: CommandPaletteProps = {}) {
  const router = useRouter();
  const inputId = useId();
  const { isOpen, setOpen } = usePaletteOpenState({ open: controlledOpen, onOpenChange });
  const view = usePaletteView();
  const { workers, setWorkers, kbPages, loadingKb } = usePaletteData(isOpen);
  const { inputRef, kbInputRef } = usePaletteFocus({ isOpen, viewMode: view.viewMode });
  const actions = usePaletteActions({ workers, setWorkers, setOpen });

  // Every open starts from an empty root list with no leftover toast.
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (wasOpen !== isOpen) {
    setWasOpen(isOpen);
    if (isOpen) {
      view.reset();
      actions.dismissToast();
    }
  }

  const { activeGroups, flattenedItems } = usePaletteItems({
    workers,
    search: view.search,
    viewMode: view.viewMode,
    setOpen,
    setViewMode: view.setViewMode,
    setSelectedIndex: view.setSelectedIndex,
    setSearch: view.setSearch,
    handleToggleAllWorkers: actions.handleToggleAllWorkers,
    handleTriggerWorker: actions.handleTriggerWorker,
  });

  // Escape backs out of a sub-screen first, and closes from the root.
  function handleEscape() {
    if (view.viewMode !== "root") {
      view.setViewMode("root");
      view.setSearch("");
      view.setSelectedIndex(0);
    } else {
      setOpen(false);
    }
  }
  const { listRef, handleKeyDown } = usePaletteKeyboard({
    items: flattenedItems,
    selectedIndex: view.selectedIndex,
    setSelectedIndex: view.setSelectedIndex,
    onEscape: handleEscape,
  });

  function submitKbSearch() {
    const q = view.kbQuery.trim();
    setOpen(false);
    if (q) {
      router.push(`/knowledge?page=${encodeURIComponent(q)}`);
    } else {
      router.push("/knowledge");
    }
  }

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center p-3 pt-12 sm:pt-20"
      role="dialog"
      aria-modal="true"
      aria-labelledby={inputId}
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/75 backdrop-blur-sm transition-opacity animate-in fade-in duration-150"
        onClick={() => setOpen(false)}
        aria-hidden="true"
      />

      {/* Palette Modal Box */}
      <div
        className="relative z-10 flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-950 shadow-2xl ring-1 ring-zinc-700/50"
        onKeyDown={handleKeyDown}
      >
        <PaletteToast message={actions.toastMessage} onDismiss={actions.dismissToast} />

        {view.viewMode === "kb-search" ? (
          <KnowledgeSearchView
            query={view.kbQuery}
            onQueryChange={view.setKbQuery}
            onSubmit={submitKbSearch}
            onBack={() => view.setViewMode("root")}
            pages={kbPages}
            loading={loadingKb}
            onOpenPage={(slug) => {
              setOpen(false);
              router.push(`/knowledge?page=${encodeURIComponent(slug)}`);
            }}
            inputRef={kbInputRef}
          />
        ) : (
          /* Normal / Run Worker View */
          <>
            <PaletteSearchHeader
              viewMode={view.viewMode}
              search={view.search}
              inputId={inputId}
              inputRef={inputRef}
              onSearchChange={(value) => {
                view.setSearch(value);
                view.setSelectedIndex(0);
              }}
              onClear={() => view.setSearch("")}
              onBack={() => {
                view.setViewMode("root");
                view.setSearch("");
              }}
            />
            <PaletteResults
              groups={activeGroups}
              items={flattenedItems}
              selectedIndex={view.selectedIndex}
              search={view.search}
              disabled={actions.statusActionPending}
              onHover={view.setSelectedIndex}
              listRef={listRef}
            />
          </>
        )}

        <PaletteFooter />
      </div>
    </div>
  );
}

export default CommandPalette;
